import { UseCase } from '@smile-next/uikit';
import { CloudIdEmptyError } from '@/errors';
import { ErrorBase } from '@/errors/ErrorBase';
import { Auth, cognitoAuth } from '@/infra/auth/Auth';
import { AuthParamsRepository, authParamsRepository } from '@/infra/repository/AuthParamsRepository';
import { SSOParamsRepository, ssoParamsRepository } from '@/infra/repository/SSOParamsRepository';
import { AuthParamsConverter } from '@/models/domain/AuthParams';
import { CloudId } from '@/models/domain/CloudId';
import { SSOParamsConverter } from '@/models/domain/SSOParams';
import { AuthenticatedFailurePayload } from '@/usecases/payload/AuthenticatedFailurePayload';
import { UpdatedCloudIdFormPayload } from '@/usecases/payload/UpdatedCloudIdFormPayload';

type LoginSSOUsecaseArgs = {
    authApi?: Auth;
    authParamsRepository?: AuthParamsRepository;
    ssoParamsRepository?: SSOParamsRepository;
};

type LoginSSOUsecaseProps = {
    cloudId?: CloudId;
    origin?: string;
    close?: boolean;
};

export class LoginSSOUseCase extends UseCase {
    private infra: Required<LoginSSOUsecaseArgs>;

    constructor(args: LoginSSOUsecaseArgs = {}) {
        super();
        this.infra = {
            authApi: cognitoAuth,
            ssoParamsRepository,
            authParamsRepository,
            ...args,
        };
    }

    async execute(props: LoginSSOUsecaseProps) {
        const { cloudId, origin, close } = props;
        const inputAuthParams = AuthParamsConverter.fromJSON({ cloudId: cloudId?.value });
        const savedAuthParams = this.infra.authParamsRepository.get();
        const authParams = savedAuthParams.join(inputAuthParams);
        const ssoParams = SSOParamsConverter.fromJSON({ origin, close });

        this.dispatch(new UpdatedCloudIdFormPayload(authParams));

        if (!authParams.hasCloudId()) {
            // クラウドIDが指定されていない場合は失敗
            this.dispatch(
                new AuthenticatedFailurePayload({
                    authParams,
                    error: new CloudIdEmptyError(),
                }),
            );
            return Promise.resolve();
        }

        // 認証完了時に遷移用の情報を保持する
        // 認証の途中で終了した場合など前回情報が積まれている事があるため、必ず上書きするようにする

        // origin情報を保存する
        await this.infra.ssoParamsRepository.save(ssoParams);

        // loginMethodはないが一旦保持する
        await this.infra.authParamsRepository.save(authParams);

        // 認証処理実行
        try {
            await this.infra.authApi.loginWithSSO(authParams.cloudId?.value);
        } catch (error) {
            if (error instanceof ErrorBase) {
                this.dispatch(
                    new AuthenticatedFailurePayload({
                        authParams,
                        error,
                    }),
                );
            }
        }

        return Promise.resolve();
    }
}
